import { Dialog } from "@headlessui/react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import { FC, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useUpdateVmetaCanvas } from "@via/pages/projects/projects.api.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import {
  addUserGroupMemberOptions,
  requiredValidation,
  vmetaNameValidation,
} from "@via/utils/form-validators";
import SelectComponent from "@via/components/select/select";
import {
  PREMIUM_DATASET_PERMISSION_OPTIONS,
  USER_ROLES,
} from "@via/utils/constants";
import { useAuth } from "@via/hooks/useAuth";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import {
  deduplicatePermissions,
  filterPermissions,
  transformPermissions,
} from "@via/pages/data/premium-dataset/utils/helper";
import { VmetaPermission, type VmetaProject, VmetaPermissionV2 } from "common";
import { Tooltip } from "react-tooltip";

type SampleTrackerType = VmetaProject & {
  owner: {
    name: string;
  };
  createdAt: Date;
  updatedAt: Date;
};

interface EditSampleTrackerFormProps {
  setOpen: (open: boolean) => void;
  sampleTracker: SampleTrackerType | null;
}

const everyoneOption = { value: "everyone", label: "Everyone" };

const defaultPermission = {
  type: "user",
  email: "",
  read: true,
  write: false,
};

const EditSampleTrackerForm: FC<EditSampleTrackerFormProps> = ({
  setOpen,
  sampleTracker,
}) => {
  const { user } = useAuth();
  const handleClose = () => setOpen(false);

  const {
    mutate: updateSampleTracker,
    isPending: isUpdateSampleTrackerLoading,
    isSuccess: isUpdateSampleTrackerSuccess,
  } = useUpdateVmetaCanvas();

  useEffect(() => {
    isUpdateSampleTrackerSuccess && handleClose();
  }, [isUpdateSampleTrackerSuccess]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{
    name: string;
    label: string;
    perms: any[];
    dataPerms: any[];
    applyToAllCollections: boolean;
  }>({
    mode: "onSubmit",
    defaultValues: {
      name: sampleTracker?.name || "",
      label: sampleTracker?.label || "",
      perms: (sampleTracker?.perms as any[]) || [],
      dataPerms: (sampleTracker?.dataPerms as any[]) || [],
      applyToAllCollections: false,
    },
  });

  const permissionOptions = useMemo(() => {
    if (user && user?.userRole === USER_ROLES.ADMIN) {
      return [everyoneOption, ...PREMIUM_DATASET_PERMISSION_OPTIONS];
    } else {
      return PREMIUM_DATASET_PERMISSION_OPTIONS;
    }
  }, [user]);

  const missingPermIds = useMemo(() => {
    return (
      (sampleTracker?.perms as VmetaPermissionV2)
        ?.filter(perm => perm.type === "group")
        .map(perm => perm.groupId) || []
    );
  }, [sampleTracker]);

  const missingDataPermIds = useMemo(() => {
    return (
      (sampleTracker?.dataPerms as VmetaPermissionV2)
        ?.filter(perm => perm.type === "group")
        .map(perm => perm.groupId) || []
    );
  }, [sampleTracker]);

  const {
    fields,
    append: appendPerms,
    remove: removePerms,
  } = useFieldArray({
    control,
    name: "perms",
  });

  const {
    fields: dataPermsFields,
    append: appendDataPerms,
    remove: removeDataPerms,
  } = useFieldArray({
    control,
    name: "dataPerms",
  });

  const handleUpdateSampleTracker = async payload => {
    if (!sampleTracker?._id) return;

    const transformedPerms = transformPermissions(payload.perms);
    const filteredPerms = filterPermissions(transformedPerms);
    const uniquePerms = deduplicatePermissions(filteredPerms);

    const transformedDataPerms = transformPermissions(payload.dataPerms);
    const filteredDataPerms = filterPermissions(transformedDataPerms);
    const uniqueDataPerms = deduplicatePermissions(filteredDataPerms);

    updateSampleTracker({
      id: sampleTracker._id as string,
      data: {
        name: payload.name,
        label: payload.label,
        perms: uniquePerms as VmetaPermission,
        dataPerms: uniqueDataPerms as VmetaPermission,
        applyToAllCollections: payload.applyToAllCollections,
      },
    });
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleUpdateSampleTracker)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Edit Sample Tracker
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Update the information below to modify your sample tracker.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-2 sm:px-4">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Name (used in API URLs)"
                placeholder="e.g. rna_seq_v1"
                showRequiredField
                showTooltipIcon
                tooltipText={
                  'Used in API routes and URLs.<br/>Letters/numbers/spaces/-/_ only. No dots.<br/>Spaces become "_" and uppercase becomes lowercase.'
                }
                validations={vmetaNameValidation("Name (used in API URLs)")}
              />
              <FormInputComponent
                control={control}
                name="label"
                label="Label (Display name)"
                placeholder="e.g. RNA-seq (v1)"
                showRequiredField
                showTooltipIcon
                tooltipText={
                  "Shown in the UI (tabs, dropdowns). Any characters allowed."
                }
                validations={requiredValidation("Label")}
              />
              <div className="flex justify-between text-center">
                <div className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Tracker Visibility & Configuration
                  <button
                    type="button"
                    id="edit-sample-tracker-admin-perms-tooltip"
                    className="ml-1 text-gray-400 hover:text-gray-600">
                    <InformationCircleIcon className="h-5 w-5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => appendPerms(defaultPermission)}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  + Add Permission
                </button>
              </div>
              <Tooltip
                anchorSelect="#edit-sample-tracker-admin-perms-tooltip"
                place="bottom"
                className="z-50 !max-w-sm">
                <div className="text-sm">
                  <p>Controls who can view this tracker.</p>
                  <p>
                    Write access also allows managing its configuration,
                    including collections, fields, and permissions.
                  </p>
                </div>
              </Tooltip>
              <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
                {fields.map((field, idx) => {
                  const type = watch(`perms.${idx}.type`);
                  const isEveryone = type === "everyone";
                  return (
                    <div
                      key={field.id}
                      className="flex items-center gap-2 border-b py-2">
                      <div className="min-w-[150px]">
                        <SelectComponent
                          control={control}
                          name={`perms.${idx}.type`}
                          options={permissionOptions}
                          placeholder="Select type"
                          enableClear={false}
                        />
                      </div>
                      {type === "user" && (
                        <div className="-mt-2 w-full">
                          <FormInputComponent
                            control={control}
                            name={`perms.${idx}.email`}
                            placeholder="Enter email"
                            validations={addUserGroupMemberOptions.email}
                          />
                        </div>
                      )}
                      {type === "group" && (
                        <div className="w-full">
                          <UserGroupSelectComponent
                            control={control}
                            name={`perms.${idx}.groupId`}
                            missingId={missingPermIds}
                            rules={requiredValidation("User Group")}
                            menuPlacement="top"
                            errors={errors}
                            uniqueIdentifier="vmetaGroupId"
                            disableIfNotFound={true}
                          />
                        </div>
                      )}
                      <label className="flex items-center gap-1">
                        <FormInputComponent
                          control={control}
                          name={`perms.${idx}.read`}
                          type="checkbox"
                          className="h-6 w-6 -mt-2"
                        />
                        Read
                      </label>
                      {!isEveryone && (
                        <label className="flex items-center gap-1">
                          <FormInputComponent
                            control={control}
                            name={`perms.${idx}.write`}
                            type="checkbox"
                            className="h-6 w-6 mr-2 -mt-2"
                          />
                          Write
                        </label>
                      )}
                      <button
                        type="button"
                        onClick={() => removePerms(idx)}
                        className="px-2">
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-center">
                <div className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Stored Data Access & Modification
                  <button
                    type="button"
                    id="edit-sample-tracker-data-perms-tooltip"
                    className="ml-1 text-gray-400 hover:text-gray-600">
                    <InformationCircleIcon className="h-5 w-5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => appendDataPerms(defaultPermission)}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  + Add Permission
                </button>
              </div>
              <Tooltip
                anchorSelect="#edit-sample-tracker-data-perms-tooltip"
                place="bottom"
                className="z-50 !max-w-sm">
                <div className="text-sm">
                  <p>
                    Controls who can view and modify data stored in this
                    tracker.
                  </p>
                  <p>
                    Write permission includes creating, editing, and deleting
                    items.
                  </p>
                </div>
              </Tooltip>
              <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
                {dataPermsFields.map((field, idx) => {
                  const type = watch(`dataPerms.${idx}.type`);
                  const isEveryone = type === "everyone";
                  return (
                    <div
                      key={field.id}
                      className="flex items-center gap-2 border-b py-2">
                      <div className="min-w-[150px]">
                        <SelectComponent
                          control={control}
                          name={`dataPerms.${idx}.type`}
                          options={permissionOptions}
                          placeholder="Select type"
                          enableClear={false}
                        />
                      </div>
                      {type === "user" && (
                        <div className="w-full">
                          <FormInputComponent
                            control={control}
                            name={`dataPerms.${idx}.email`}
                            placeholder="Enter email"
                            validations={addUserGroupMemberOptions.email}
                          />
                        </div>
                      )}
                      {type === "group" && (
                        <div className="w-full">
                          <UserGroupSelectComponent
                            control={control}
                            name={`dataPerms.${idx}.groupId`}
                            missingId={missingDataPermIds}
                            rules={requiredValidation("User Group")}
                            menuPlacement="top"
                            errors={errors}
                            uniqueIdentifier="vmetaGroupId"
                            disableIfNotFound={true}
                          />
                        </div>
                      )}
                      <label className="flex items-center gap-1">
                        <FormInputComponent
                          control={control}
                          name={`dataPerms.${idx}.read`}
                          type="checkbox"
                          className="h-6 w-6 -mt-2"
                        />
                        Read
                      </label>
                      {!isEveryone && (
                        <label className="flex items-center gap-1">
                          <FormInputComponent
                            control={control}
                            name={`dataPerms.${idx}.write`}
                            type="checkbox"
                            className="h-6 w-6 mr-2 -mt-2"
                          />
                          Write
                        </label>
                      )}
                      <button
                        type="button"
                        onClick={() => removeDataPerms(idx)}
                        className="px-2">
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2">
                <label className="inline-flex items-center w-auto">
                  <FormInputComponent
                    control={control}
                    name="applyToAllCollections"
                    type="checkbox"
                    className="h-6 w-6 mr-2 -mt-2"
                  />
                  <span className="text-sm font-medium leading-6 text-gray-900">
                    Apply permissions to all collections
                  </span>
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Apply this tracker&apos;s visibility and configuration
                  permissions and stored data access and modification
                  permissions to every collection. Data created in any
                  collection will also inherit these stored data access and
                  modification permissions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-sample-tracker-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-sample-tracker-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateSampleTrackerLoading}>Update</Spinner>
        </button>
      </div>
    </form>
  );
};

export default EditSampleTrackerForm;
